import { locateProxy } from '#~/agent/utils.js';
import { useAudioTrack, useRoomLiveKit, useVideoTrack } from '#~/livekit/hooks.js';
import {
  acquireRoomTrackStats,
  createDefaultDeviceStats,
  createRoomTrackStatsIdentity,
  resetRoomTrackStatsForTests,
  type DeviceStats,
  type RoomTrackStatsLease,
  type RoomTrackStatsMediaType,
  type StatsManager
} from '#~/livekit/room-track-stats.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { useEffect, useState } from 'react';

export type { DeviceStats, StatsManager };

const REFRESH_INTERVAL_DEFAULT = 500;
type MediaStatsType = 'auto' | RoomTrackStatsMediaType;

/** @internal Legacy reset name retained for source compatibility. */
export const resetDeviceStatsManagers = resetRoomTrackStatsForTests;

export const useDeviceStats = (
  roomId: Room['id'],
  deviceId: Device['id'],
  refreshInterval = REFRESH_INTERVAL_DEFAULT,
  mediaType: MediaStatsType = 'auto'
): DeviceStats => {
  const videoTrack = useVideoTrack(deviceId, roomId);
  const audioTrack = useAudioTrack(deviceId, roomId);
  const track =
    mediaType === 'video'
      ? videoTrack
      : mediaType === 'audio'
        ? audioTrack
        : (videoTrack ?? audioTrack);
  const resolvedMediaType: RoomTrackStatsMediaType =
    mediaType === 'auto' ? (videoTrack ? 'video' : 'audio') : mediaType;
  const [stats, setStats] = useState<DeviceStats>(createDefaultDeviceStats());

  useEffect(() => {
    if (!track) {
      setStats(createDefaultDeviceStats());
      return;
    }

    const lease = acquireRoomTrackStats({
      identity: createRoomTrackStatsIdentity(roomId, deviceId, resolvedMediaType, track),
      track,
      refreshInterval,
      listener: setStats
    });

    return lease.release;
  }, [deviceId, refreshInterval, resolvedMediaType, roomId, track]);

  return stats;
};

export const useVideoDeviceStats = (
  roomId: Room['id'],
  deviceId: Device['id'],
  refreshInterval = REFRESH_INTERVAL_DEFAULT
): DeviceStats => useDeviceStats(roomId, deviceId, refreshInterval, 'video');

export const useAudioDeviceStats = (
  roomId: Room['id'],
  deviceId: Device['id'],
  refreshInterval = REFRESH_INTERVAL_DEFAULT
): DeviceStats => useDeviceStats(roomId, deviceId, refreshInterval, 'audio');

export const usePremountDeviceStats = (
  roomId: Room['id'],
  refreshInterval = REFRESH_INTERVAL_DEFAULT
): void => {
  const deviceMap = useMultiDeviceStateQuery<{ id: Device['id'] }>({
    transform: (device) => ({ id: device.id })
  });
  const liveKit = useRoomLiveKit(roomId);

  useEffect(() => {
    const leases: RoomTrackStatsLease[] = [];

    for (const unsafeDeviceId of Object.keys(deviceMap)) {
      const deviceId = unsafeDeviceId as Device['id'];
      const proxy = locateProxy(deviceId);

      if (!proxy) {
        continue;
      }

      const videoTrack = liveKit?.videoForAgent(proxy.id, proxy.embedded).get(deviceId) ?? null;
      const audioTrack = liveKit?.audioForAgent(proxy.id, proxy.embedded).get(deviceId) ?? null;

      if (videoTrack) {
        leases.push(
          acquireRoomTrackStats({
            identity: createRoomTrackStatsIdentity(roomId, deviceId, 'video', videoTrack),
            track: videoTrack,
            refreshInterval
          })
        );
      }

      if (audioTrack) {
        leases.push(
          acquireRoomTrackStats({
            identity: createRoomTrackStatsIdentity(roomId, deviceId, 'audio', audioTrack),
            track: audioTrack,
            refreshInterval
          })
        );
      }
    }

    return () => {
      for (const lease of leases) {
        lease.release();
      }
    };
  }, [deviceMap, liveKit, refreshInterval, roomId]);
};
